import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  ActionMenu,
  IconButton,
  IconMoreVert,
  LinkButton,
  Tooltip,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useMemo } from 'react';
import { SingleDeleteRecord } from '@/features/invoice_reports/components/ConfirmDeleteModal/context/ConfirmDeleteModalContext';
import type { DuplicateParams } from '@/features/invoice_reports/components/ConfirmDuplicateModal/context/ConfirmDuplicateModalContext';
import type { CancelRecord } from '@/features/invoice_reports/components/ConfirmCancelModal/context/ConfirmCancelModalContext';
import type { ReportActionState } from '@/features/invoice_reports/types';
import { useApplicationListActionMenu } from '../../hooks/useApplicationListActionMenu';
import styles from './ActionCell.module.scss';

const cx = classnames.bind(styles);

export interface ActionCellRecord {
  id: string;
  detail_path: string;
  edit_path: string;
  copy_path: string;
  status: { key?: string };
  is_copyable: boolean;
  edit_action: ReportActionState;
  delete_action: ReportActionState;
  cancel_action: ReportActionState;
  /** Share button state for this invoice report; undefined where the list API doesn't return it yet. */
  share_action?: ReportActionState;
  /**
   * Current viewer count; pending backend support
   * (`invoice_reports.action_menu.duplicate_with_same_viewers` is disabled
   * whenever this is falsy).
   */
  viewers_count?: number | null;
  /** For confirm_copy message (invoice_reports/_list, ex_report_types) */
  title?: string;
  number?: string;
  ap_payee_name?: string | null;
  total_amount?: string | null;
}

export type ActionCellProps = {
  record: ActionCellRecord;
  onDuplicate?: (params: DuplicateParams) => Promise<void>;
  onDelete?: (record: SingleDeleteRecord) => void;
  onCancel?: (record: CancelRecord) => void;
  onShare?: (record: ActionCellRecord) => void;
};

/**
 * Action cell for 申請＞支払依頼一覧 (NA-4165).
 * Edit (visible/allowed) and 3-dot menu: 詳細, 共有, 複製, 削除, 取消し driven by
 * edit_action, delete_action, cancel_action, share_action.
 * Tooltips from action reason when disabled.
 */
export const ActionCell: FC<ActionCellProps> = memo(
  ({ record, onDuplicate, onDelete, onCancel, onShare }) => {
    const { t } = useTranslation();
    const dropDownProps = useMemo(() => ({ destroyPopupOnHide: true }), []);
    const container = useGlobalContainerRef();
    const {
      isEditVisible,
      isEditDisabled,
      editHref,
      menuItems,
      editDisabledTooltip,
      deleteMenuItemTooltip,
      cancelMenuItemTooltip,
    } = useApplicationListActionMenu({
      record: {
        id: record.id,
        detail_path: record.detail_path,
        edit_path: record.edit_path,
        copy_path: record.copy_path,
        status: record.status,
        is_copyable: record.is_copyable,
        edit_action: record.edit_action ?? {
          visible: false,
          allowed: false,
          reason: null,
        },
        delete_action: record.delete_action ?? {
          visible: false,
          allowed: false,
          reason: null,
        },
        cancel_action: record.cancel_action ?? {
          visible: false,
          allowed: false,
          reason: null,
        },
        share_action: record.share_action,
        viewers_count: record.viewers_count,
        title: record.title,
        number: record.number,
        ap_payee_name: record.ap_payee_name,
        total_amount: record.total_amount,
      },
      t,
      onShare,
      onDuplicate,
      onDelete,
      onCancel,
    });

    const menuItemsWithStyleAndTooltips = useMemo(() => {
      const toTooltipTitle = (tooltip: string | undefined) =>
        tooltip ? t(tooltip) : undefined;
      return menuItems.map((item) => {
        // Group/divider entries (the 複製 group) have no `key`/`label` to
        // rewrite here, so pass them through untouched.
        if (!item || !('key' in item) || !('label' in item)) {
          return item;
        }
        if (item.key === 'detail') {
          return {
            ...item,
            label: (
              <a href={record.detail_path} className={cx('detailLink')}>
                {item.label}
              </a>
            ),
            onClick: undefined,
          };
        }
        const disabledLabelClass =
          item.key === 'cancel' && cancelMenuItemTooltip
            ? 'cancelLabelDisabled'
            : item.key === 'delete' && deleteMenuItemTooltip
            ? 'cancelLabelDisabled'
            : 'cancelLabel';
        const labelSpan = (
          <span className={cx(disabledLabelClass)}>{item.label}</span>
        );
        if (item.key === 'cancel' && cancelMenuItemTooltip) {
          return {
            ...item,
            label: (
              <Tooltip
                title={toTooltipTitle(cancelMenuItemTooltip)}
                overlayClassName={cx(styles['actionCellTooltip'])}
              >
                <div>{labelSpan}</div>
              </Tooltip>
            ),
          };
        }
        if (item.key === 'delete' && deleteMenuItemTooltip) {
          return {
            ...item,
            label: (
              <Tooltip
                title={toTooltipTitle(deleteMenuItemTooltip)}
                overlayClassName={cx(styles['actionCellTooltip'])}
              >
                <div>{labelSpan}</div>
              </Tooltip>
            ),
          };
        }
        if (item.key === 'delete' || item.key === 'cancel') {
          return { ...item, label: labelSpan };
        }
        return item;
      });
    }, [
      menuItems,
      cancelMenuItemTooltip,
      deleteMenuItemTooltip,
      t,
      record.detail_path,
    ]);

    const editNode = useMemo(
      () =>
        !isEditVisible ? null : isEditDisabled ? (
          editDisabledTooltip ? (
            <Tooltip
              title={editDisabledTooltip ? t(editDisabledTooltip) : undefined}
              overlayClassName={cx(styles['actionCellTooltip'])}
              placement='leftBottom'
            >
              <span className={cx('editDisabled')}>{t('edit')}</span>
            </Tooltip>
          ) : (
            <span className={cx('editDisabled')}>{t('edit')}</span>
          )
        ) : (
          <LinkButton
            containerClassName={cx('editButton')}
            isTransparent
            href={editHref}
          >
            {t('edit')}
          </LinkButton>
        ),
      [isEditVisible, isEditDisabled, editDisabledTooltip, t, editHref]
    );

    return (
      <div
        className={cx(styles['container'])}
        onClick={(e) => e.stopPropagation()}
      >
        {editNode}
        <ActionMenu
          type='custom'
          items={menuItemsWithStyleAndTooltips}
          dropDownProps={dropDownProps}
          getPopupContainer={container}
          className={cx('actionMenu')}
        >
          <IconButton size='xs' inVisible>
            <IconMoreVert size={20} />
          </IconButton>
        </ActionMenu>
      </div>
    );
  }
);

ActionCell.displayName = 'ActionCell';
